import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { componentsApi, ApiError } from '../../api/client';
import { INVISIBLE_COMPONENT_TYPES } from '../InvisibleComponentsBar';
import type { Component, ComponentResponse } from '../../schemas/validation';
import type { ComponentManagerState } from './types';
import { VALID_COMPONENT_TYPES, getApiBaseUrl } from './constants';

/**
 * Filter to get only visible (non-invisible) components
 * Invisible components like head-content and body-end are managed separately
 */
const getVisibleComponents = (components: Component[]): Component[] =>
  components.filter(c => !INVISIBLE_COMPONENT_TYPES.includes(c.type));

interface UseComponentManagerHandlersProps {
  pageId: string;
  state: ComponentManagerState;
  onComponentSaved?: () => void;
  onComponentsChanged?: (components: Component[]) => void;
}

export const useComponentManagerHandlers = ({
  pageId,
  state,
  onComponentSaved,
  onComponentsChanged,
}: UseComponentManagerHandlersProps) => {
  const loadComponents = useCallback(async () => {
    try {
      // Only show loading spinner on initial load, not on refresh
      // This prevents scroll position from resetting when adding components
      if (!state.hasInitiallyLoaded) {
        state.setIsLoading(true);
      }
      state.setError(null);
      const response: ComponentResponse = await componentsApi.getPageComponents(pageId);

      console.log('Loaded components from API:', response);

      // Normalize: some types (notably html-embed) may arrive with missing/null data until first save.
      // Runtime replacement `{}` preserves behavior; `as Component[]` keeps the discriminated union
      // (Vercel's `tsc -b` rejects `data: Record<string, any> | …` on `setComponents`).
      const normalized = response.components.map(c => ({
        ...c,
        data: c.data !== null && typeof c.data === 'object' && !Array.isArray(c.data) ? c.data : {},
      })) as Component[];

      // Sort by order and filter out invalid components only
      // NOTE: We keep invisible components (head-content, body-end) in the array
      // so that insertion positions are calculated correctly.
      // Invisible components are filtered out at render time in ComponentsList.
      const sortedComponents = normalized
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter(component => {
          if (!component.id || !component.type) {
            console.warn('Invalid component found, skipping:', component);
            return false;
          }
          if (!VALID_COMPONENT_TYPES.includes(component.type as any)) {
            console.warn('Unknown component type found:', component.type);
            return false;
          }
          return true;
        });

      console.log('Sorted and validated components:', sortedComponents);
      console.log(
        'Invisible components in array:',
        sortedComponents.filter(c => INVISIBLE_COMPONENT_TYPES.includes(c.type)).map(c => c.type)
      );
      logFeaturesComponents(sortedComponents);
      state.setComponents(sortedComponents);
      onComponentsChanged?.(sortedComponents);
    } catch (err) {
      console.error('Failed to load components:', err);
      if (err instanceof ApiError) {
        state.setError(err.message);
      } else {
        state.setError('Failed to load components');
      }
    } finally {
      state.setIsLoading(false);
      state.setHasInitiallyLoaded(true);
    }
  }, [pageId, state, onComponentsChanged]);

  const handleDeleteComponent = useCallback(
    (componentId: string) => {
      const component = state.components.find(c => c.id === componentId);
      if (!component) return;
      state.setComponentToDelete(component);
      state.setIsDeleteModalOpen(true);
    },
    [state]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!state.componentToDelete) return;

    try {
      state.setIsDeleting(true);
      state.setDeleteError(null);
      await componentsApi.deleteComponent(pageId, state.componentToDelete.id);
      const updated = state.components.filter(c => c.id !== state.componentToDelete!.id);
      state.setComponents(updated);
      onComponentsChanged?.(updated);
      state.setIsDeleteModalOpen(false);
      state.setComponentToDelete(null);
      onComponentSaved?.();
    } catch (err) {
      console.error('Failed to delete component:', err);
      // Keep the component list mounted. A fatal page error hides every component,
      // including the one the user is trying to delete.
      state.setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete component');
    } finally {
      state.setIsDeleting(false);
    }
  }, [pageId, state, onComponentSaved, onComponentsChanged]);

  const handleCancelDelete = useCallback(() => {
    state.setIsDeleteModalOpen(false);
    state.setComponentToDelete(null);
    state.setDeleteError(null);
  }, [state]);

  const handleMoveComponent = useCallback(
    async (componentId: string, direction: 'up' | 'down') => {
      // Get the visible components (for determining swap targets)
      const visibleComponents = getVisibleComponents(state.components);
      const visibleIndex = visibleComponents.findIndex(c => c.id === componentId);
      if (visibleIndex === -1) return;

      // Find the target visible component to swap with
      const targetVisibleIndex = direction === 'up' ? visibleIndex - 1 : visibleIndex + 1;
      if (targetVisibleIndex < 0 || targetVisibleIndex >= visibleComponents.length) return;

      const targetComponent = visibleComponents[targetVisibleIndex];

      // Get actual indices in the full components array
      const currentActualIndex = state.components.findIndex(c => c.id === componentId);
      const targetActualIndex = state.components.findIndex(c => c.id === targetComponent.id);

      if (currentActualIndex === -1 || targetActualIndex === -1) return;

      // Swap the two components in the array
      const reorderedComponents = [...state.components];
      [reorderedComponents[currentActualIndex], reorderedComponents[targetActualIndex]] = [
        reorderedComponents[targetActualIndex],
        reorderedComponents[currentActualIndex],
      ];

      // Recalculate order values for all components
      const componentOrders = reorderedComponents.map((component, index) => ({
        componentId: component.id,
        order: index,
      }));

      const requestData = { componentOrders };
      const authToken = localStorage.getItem('authToken');

      console.log('Reordering components:', {
        pageId,
        componentId,
        direction,
        visibleIndex,
        targetVisibleIndex,
        currentActualIndex,
        targetActualIndex,
        componentOrders,
        requestData,
      });

      try {
        const response = await fetch(`${getApiBaseUrl()}/pages/${pageId}/components/reorder`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken || ''}`,
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Reorder API Error:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
          });

          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { message: errorText };
          }

          throw new ApiError(
            errorData.message || `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            errorData
          );
        }

        const result = await response.json();
        console.log('Reorder SUCCESS:', result);

        const updatedComponents = reorderedComponents.map((component, index) => ({
          ...component,
          order: index,
        }));

        console.log('Updating local state with reordered components:', updatedComponents);
        state.setComponents(updatedComponents);
        onComponentsChanged?.(updatedComponents);
        onComponentSaved?.();
      } catch (err) {
        console.error('Failed to reorder components:', err);
        state.setError(err instanceof ApiError ? err.message : 'Failed to reorder components');
      }
    },
    [pageId, state, onComponentSaved, onComponentsChanged]
  );

  const handleEditComponent = useCallback(
    (component: Component) => {
      state.setEditingComponent(component);
      state.setIsEditModalOpen(true);
    },
    [state]
  );

  const handleCloseEditModal = useCallback(() => {
    state.setIsEditModalOpen(false);
    state.setEditingComponent(null);
  }, [state]);

  const handleSaveToLibrary = useCallback(
    (component: Component) => {
      state.setComponentToSave(component);
      state.setIsSaveToLibraryModalOpen(true);
    },
    [state]
  );

  const handleSaveToLibrarySuccess = useCallback(() => {
    console.log('Component saved to library successfully');
  }, []);

  const handleDuplicateComponent = useCallback(
    async (component: Component) => {
      if (component.type === 'conversion-popup') {
        state.setError(
          'Only one conversion popup is allowed per page. Remove the existing one before adding another.'
        );
        return;
      }
      try {
        // Deep-clone the component data and regenerate any nested UUIDs
        const clonedData = regenerateIds(JSON.parse(JSON.stringify(component.data)));

        const position = component.order + 1;

        console.log('Duplicating component:', {
          originalId: component.id,
          type: component.type,
          position,
        });

        const result = await componentsApi.addTemplateComponent(
          pageId,
          component.type,
          clonedData,
          position
        );

        console.log('Component duplicated successfully:', result);

        // Reload components to get the updated list with correct ordering
        await loadComponents();

        // Scroll to the newly created component after the DOM updates
        const newComponentId = result.component?.id;
        if (newComponentId) {
          setTimeout(() => {
            const el = document.getElementById(`section-${newComponentId}`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }

        onComponentSaved?.();
      } catch (err) {
        console.error('Failed to duplicate component:', err);
        state.setError(err instanceof ApiError ? err.message : 'Failed to duplicate component');
      }
    },
    [pageId, state, loadComponents, onComponentSaved]
  );

  return {
    loadComponents,
    handleDeleteComponent,
    handleConfirmDelete,
    handleCancelDelete,
    handleMoveComponent,
    handleEditComponent,
    handleCloseEditModal,
    handleSaveToLibrary,
    handleSaveToLibrarySuccess,
    handleDuplicateComponent,
  };
};

/**
 * UUID v4 regex pattern for identifying fields that contain UUIDs
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Recursively walk through component data and regenerate any UUID `id` fields.
 * This ensures duplicated components don't share IDs with the original's nested items
 * (e.g. image-info items, pricing plans, feature items, etc.).
 */
function regenerateIds(data: any): any {
  if (Array.isArray(data)) {
    return data.map(item => regenerateIds(item));
  }
  if (data && typeof data === 'object') {
    const result = { ...data };
    for (const key of Object.keys(result)) {
      if (key === 'id' && typeof result[key] === 'string' && UUID_REGEX.test(result[key])) {
        result[key] = uuidv4();
      } else if (typeof result[key] === 'object' && result[key] !== null) {
        result[key] = regenerateIds(result[key]);
      }
    }
    return result;
  }
  return data;
}

function logFeaturesComponents(components: Component[]) {
  const featuresComponents = components.filter(c => c.type === 'features');
  if (featuresComponents.length > 0) {
    featuresComponents.forEach(fc => {
      console.log('🖼️ ComponentManager: Loaded features component from API:', {
        componentId: fc.id,
        displayMode: (fc.data as any).displayMode,
        featuresCount: (fc.data as any).features?.length,
        features: (fc.data as any).features?.map((f: any, idx: number) => ({
          index: idx,
          title: f.title,
          hasImageUrl: !!f.imageUrl,
          hasCloudfrontUrl: !!f.cloudfrontUrl,
          hasMediaId: !!f.mediaId,
          hasS3Key: !!f.s3Key,
          imageUrl: f.imageUrl,
          cloudfrontUrl: f.cloudfrontUrl,
          mediaId: f.mediaId,
          s3Key: f.s3Key,
          imageAlt: f.imageAlt,
        })),
      });
    });
  }
}
